import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { Grid, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { AccCodeDropdownFullWidth } from 'components/Dropdown/AccCodeDropdown'
import {
  useGetCompanyNameQuery,
  useGetCostCentreQuery,
} from 'generated/graphql'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import { amtNumStr } from 'helpers/StringNumberFunction/NumFormatters'
import { CommonYupValidation } from 'helpers/YupSchema/yup'
import React, { useEffect, useState, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { uuid } from 'uuidv4'
import * as yup from 'yup'

interface UsedDepartmentProps {
  CostCentreID: string
  Quantity: number
  UOMID: string
  fa_location_id: string
}

//Apportionment Percentage
const calculateAllApportionmentPercentages = (departments: any[]): number[] => {
  if (!departments || departments.length === 0) return []

  const totalQty = departments.reduce((sum, d) => {
    const qty = parseFloat(
      String(d.Quantity || d.quantity || 0).replace(/,/g, '')
    )
    return sum + (isNaN(qty) ? 0 : qty)
  }, 0)

  if (totalQty <= 0) return departments.map(() => 0)

  const percentages: number[] = []
  let runningTotal = 0

  for (let i = 0; i < departments.length; i++) {
    const deptQty =
      parseFloat(
        String(departments[i].Quantity || departments[i].quantity || 0).replace(
          /,/g,
          ''
        )
      ) || 0

    if (i === departments.length - 1) {
      const lastPct = 100 - runningTotal
      percentages.push(lastPct)
    } else {
      const exactPct = (deptQty / totalQty) * 100
      const pct = Math.ceil(exactPct)
      percentages.push(pct)
      runningTotal += pct
    }
  }

  return percentages
}

export const AssetsRegisterUsedDepartment = (props: any) => {
  const {
    openUsedDepartment,
    formMode,
    detailMode,
    menu,
    updateParentCalculations,
    usedDepartmentData,
    CompanyID,
    AssetsRegistrationID,
    UsedDepartmentID,
    getUOM,
    getFALocation,
    fetchUOMID,
    setFetchUOMID,
    setOpenUsedDepartment,
  } = props

  const UOMCode = getUOM?.find(x => x?.UOMID === fetchUOMID)?.Code

  const defaultVal: any = {
    CostCentreID: '',
    Quantity: 0,
    UOMID: UOMCode,
    fa_location_id: '',
  }

  let form, mode
  switch (detailMode) {
    case 'add':
      form = 'New'
      mode = 'add'
      break
    case 'edit':
      form = 'Edit'
      mode = 'edit'
  }

  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */

  const AssetsUsedDepartmentFormSchema = yup.object().shape({
    CostCentreID: CommonYupValidation.requireField(SystemMsgs.department()),
    Quantity: yup
      .number()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string') {
          const cleanedString = originalValue.replace(/,/g, '')

          if (cleanedString === '') {
            return undefined
          }

          return parseFloat(cleanedString)
        }
        return value
      })
      .moreThan(0.0, 'Quantity cannot less than 0.01')
      .typeError('Quantity is required')
      .required('Quantity is required'),
    UOMID: CommonYupValidation.requireField(SystemMsgs.uom()),
    fa_location_id: yup.string().required('Location is Required'),
  })

  const {
    handleSubmit,
    register,
    setValue,
    control,
    errors,
    reset,
    setError,
    getValues,
    formState,
    watch,
    clearErrors,
  } = useForm<UsedDepartmentProps>({
    defaultValues: {
      CostCentreID:
        detailMode === 'new' || detailMode === 'add'
          ? undefined
          : menu?.obj?.CostCentreID ?? '',
      Quantity:
        detailMode === 'new' || detailMode === 'add'
          ? 0
          : menu?.obj?.Quantity ?? 0,
      UOMID:
        detailMode === 'new' || detailMode === 'add'
          ? undefined
          : menu?.obj?.UOMID ?? '',
      fa_location_id:
        detailMode === 'new' || detailMode === 'add'
          ? undefined
          : menu?.obj?.fa_location_id ?? '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(AssetsUsedDepartmentFormSchema),
  })
  console.log('menu: ', menu)

  /* -------------------------------------------- */
  /*                   USE EFFECT                 */
  /* -------------------------------------------- */

  // Reset form values when editing
  useEffect(() => {
    if (detailMode === 'edit' && menu?.obj && openUsedDepartment) {
      setValue('CostCentreID', menu.obj.CostCentreID)
      setValue('Quantity', menu.obj.Quantity)
      setValue('UOMID', menu.obj.UOMID)
      setValue('fa_location_id', menu.obj.fa_location_id)
      setUsedQty(menu.obj.Quantity)
    }
  }, [detailMode, menu?.obj, openUsedDepartment, setValue])

  /* -------------------------------------------- */
  /*                     STATE                    */
  /* -------------------------------------------- */

  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [defaultCostCentre, loadDefaultCostCentre] = useState(null)
  const [usedQty, setUsedQty] = useState(
    amtNumStr(UsedDepartmentID && usedDepartmentData?.Quantity) ?? 0
  )
  const [countItem, setCountItem] = useState(0)

  const projectedApportionmentPct = useMemo(() => {
    const currentQty = parseFloat(String(usedQty).replace(/,/g, '')) || 0

    const projectedDepts: any[] = []
    let currentEntryIndex = -1

    if (usedDepartmentData && Array.isArray(usedDepartmentData)) {
      usedDepartmentData.forEach((dept, idx) => {
        if (detailMode === 'edit' && idx === menu?.index) {
          projectedDepts.push({ Quantity: currentQty })
          currentEntryIndex = projectedDepts.length - 1
        } else {
          projectedDepts.push(dept)
        }
      })
    }

    if (detailMode === 'add') {
      projectedDepts.push({ Quantity: currentQty })
      currentEntryIndex = projectedDepts.length - 1
    }

    const percentages = calculateAllApportionmentPercentages(projectedDepts)
    return percentages[currentEntryIndex] ?? 0
  }, [usedQty, usedDepartmentData, detailMode, menu?.index])

  const existingApportionmentBreakdown = useMemo(() => {
    if (!usedDepartmentData || !Array.isArray(usedDepartmentData)) return []

    const percentages = calculateAllApportionmentPercentages(usedDepartmentData)

    return usedDepartmentData.map((dept, idx) => {
      const deptQty = parseFloat(String(dept.Quantity).replace(/,/g, '')) || 0
      return {
        name: dept.CostCentreName || `Department ${idx + 1}`,
        quantity: deptQty,
        percentage: percentages[idx] || 0,
        isLast: idx === usedDepartmentData.length - 1,
      }
    })
  }, [usedDepartmentData])

  /* -------------------------------------------- */
  /*                     QUERY                    */
  /* -------------------------------------------- */

  const {
    loading: CompanyLoading,
    data: { getCompany } = { getCompany: [] },
  } = useGetCompanyNameQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID: CompanyID },
    onCompleted: data => {
      loadDefaultCostCentre(data?.getCompany[0]?.DefaultCostCentre)
    },
  })

  const {
    loading: CostCentreLoading,
    error: CostCentreErrors,
    data: { getCostCentre } = { getCostCentre: [] },
  } = useGetCostCentreQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID: CompanyID,
      IsLastNode: true,
    },
  })

  /* -------------------------------------------- */
  /*                   FUNCTION                   */
  /* -------------------------------------------- */

  const handleQtyChange = event => {
    setUsedQty(event.value)
  }

  const onSubmit = (data, mode, index) => {
    const getCostCentreDetails = costCentreId => {
      const costCentre = getCostCentre.find(
        cc => cc?.CostCentreID === costCentreId
      )
      return costCentre || defaultCostCentre
    }

    const getLocationDetails = locationId => {
      return getFALocation.find(loc => loc?.fa_location_id === locationId)
    }

    const costCentreDetails = getCostCentreDetails(data.CostCentreID)
    const locationDetails = getLocationDetails(data.fa_location_id)

    const entry = {
      Sequence:
        detailMode === 'add'
          ? usedDepartmentData.length + 1
          : usedDepartmentData?.Sequence ?? index + 1,
      AssetsRegistrationID: data?.AssetsRegistrationID,
      UsedDepartmentID: detailMode === 'add' ? uuid() : data?.UsedDepartmentID,
      Quantity: parseFloat(amtNumStr(data?.Quantity)) || null,
      CostCentreID: data.CostCentreID,
      CostCentreCode: costCentreDetails?.Code,
      CostCentreName: costCentreDetails?.Name,
      fa_location_id: data.fa_location_id,
      LocationName: locationDetails?.name,
      UOMID: data?.UOMID,
    }

    if (detailMode === 'add') {
      usedDepartmentData.push(entry)
    } else {
      usedDepartmentData.splice(index, 1, entry)
    }

    updateParentCalculations(usedDepartmentData)

    if (mode === 'new') {
      reset(defaultVal)
      setCountItem(countItem + 1)
    } else {
      setOpenUsedDepartment(false)
    }
  }
  return (
    <>
      {CompanyLoading && <Loading />}

      <CommonDialog
        fullWidth={true}
        open={openUsedDepartment}
        onClose={() => {
          setOpenUsedDepartment(false)
        }}
        sections={{
          header: {
            title: 'Used Department',
            rightText: detailMode === 'add' ? 'New' : 'Edit',
          },
          body: () => (
            <>
              <CardContents>
                {!CostCentreLoading && (
                  <Controller
                    render={({ value, onChange }) => {
                      const selectedCC = getCostCentre
                        ?.sort((a, b) => {
                          return a.Code.localeCompare(b.Code)
                        })
                        ?.find(cc => cc?.CostCentreID === value)

                      return (
                        <Autocomplete
                          options={getCostCentre || []}
                          value={selectedCC || null}
                          getOptionLabel={option =>
                            option ? `${option?.Code} | ${option?.Name}` : ''
                          }
                          fullWidth
                          onChange={(event, newValue: any) => {
                            onChange(newValue?.CostCentreID || '')
                          }}
                          disabled={mode === 'approve-reject'}
                          PopperComponent={AccCodeDropdownFullWidth}
                          renderOption={option => {
                            return (
                              <div>
                                <div>
                                  <span className="xsTitle">
                                    {option?.Code}
                                  </span>{' '}
                                </div>
                                <div className="desc">{option?.Name}</div>{' '}
                              </div>
                            )
                          }}
                          renderInput={(params: any) => {
                            return (
                              <div>
                                <TextField
                                  {...params}
                                  label="Department"
                                  InputLabelProps={
                                    selectedCC ? { shrink: true } : undefined
                                  }
                                  style={{ width: '100%' }}
                                  margin="normal"
                                  helperText={errors?.CostCentreID?.message}
                                  error={errors?.CostCentreID ? true : false}
                                  required
                                  disabled={mode === 'approve-reject'}
                                />
                              </div>
                            )
                          }}
                        />
                      )
                    }}
                    name="CostCentreID"
                    label="Department"
                    autoComplete="off"
                    control={control}
                    ref={register}
                    multiline={true}
                    fullWidth
                    margin="normal"
                    helperText={errors?.CostCentreID?.message}
                    error={errors?.CostCentreID ? true : false}
                    required
                    disabled={formMode === 'approve-reject' ? true : false}
                  />
                )}

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Controller
                      as={<NumberFormat allowNegative={false} />}
                      customInput={TextField}
                      id="standard-basic"
                      name="Quantity"
                      label="Quantity"
                      autoComplete="off"
                      control={control}
                      ref={register}
                      value={usedQty}
                      decimalScale={2}
                      onValueChange={e => {
                        handleQtyChange(e)
                      }}
                      thousandSeparator
                      fixedDecimalScale
                      margin="normal"
                      fullWidth
                      helperText={errors?.Quantity?.message}
                      error={errors?.Quantity ? true : false}
                      required
                      disabled={formMode === 'approve-reject' ? true : false}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Apportion (%)"
                      value={`${projectedApportionmentPct.toFixed(2)}%`}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        disableUnderline: true,
                        readOnly: true,
                      }}
                      disabled
                    />
                  </Grid>
                </Grid>

                <Controller
                  as={TextField}
                  id="standard-basic"
                  name="UOMID"
                  label="UOM"
                  autoComplete="off"
                  control={control}
                  ref={register}
                  defaultValue={UOMCode}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    disableUnderline: true,
                    readOnly: true,
                  }}
                  helperText={errors?.UOMID?.message}
                  error={!!errors?.UOMID}
                  required
                  disabled
                />

                <Controller
                  render={({ value, onChange }) => {
                    const selectedLocation = getFALocation
                      ?.sort((a, b) => a.name.localeCompare(b.name))
                      ?.find(loc => loc?.fa_location_id === value)

                    return (
                      <Autocomplete
                        options={
                          getFALocation?.sort((a, b) =>
                            a.name.localeCompare(b.name)
                          ) || []
                        }
                        value={selectedLocation || null}
                        getOptionLabel={option =>
                          option
                            ? `${option.name}${
                                option.description
                                  ? ' | ' + option.description
                                  : ''
                              }`
                            : ''
                        }
                        fullWidth
                        onChange={(event, newValue) => {
                          onChange(newValue?.fa_location_id || '')
                        }}
                        disabled={mode === 'approve-reject'}
                        PopperComponent={AccCodeDropdownFullWidth}
                        renderOption={option => (
                          <div>
                            <div>
                              <span className="xsTitle">{option?.name}</span>
                              {option?.description && (
                                <span className="xsDescription">
                                  {' '}
                                  - {option.description}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Location"
                            InputLabelProps={
                              selectedLocation ? { shrink: true } : undefined
                            }
                            margin="normal"
                            helperText={errors?.fa_location_id?.message}
                            error={!!errors?.fa_location_id}
                            required
                          />
                        )}
                      />
                    )
                  }}
                  name="fa_location_id"
                  control={control}
                  required
                />
              </CardContents>

              <ErrorDialog
                errorDia={errorDia}
                setErrorDia={setErrorDia}
                errorMsg={errMsg}
                errorHeaderMsg={'Error!'}
              />
            </>
          ),
          footer: {
            actions:
              detailMode === 'add'
                ? [
                    {
                      displayText: 'Close',
                      props: {
                        onClick: () => setOpenUsedDepartment(false),
                        variant: 'contained',
                        color: 'primary',
                      },
                    },
                    {
                      displayText: 'Confirm & New',
                      props: {
                        onClick: () => {
                          handleSubmit(data =>
                            onSubmit(data, 'new', menu?.index)
                          )()
                        },
                        variant: 'contained',
                        color: 'primary',
                      },
                    },
                    {
                      displayText: 'Confirm',
                      props: {
                        onClick: () => {
                          handleSubmit(data =>
                            onSubmit(data, 'save', menu?.index)
                          )()
                        },
                        variant: 'contained',
                        color: 'primary',
                      },
                    },
                  ]
                : [
                    {
                      displayText: 'Close',
                      props: {
                        onClick: () => setOpenUsedDepartment(false),
                        variant: 'contained',
                        color: 'primary',
                      },
                    },
                    {
                      displayText: 'Confirm',
                      props: {
                        onClick: () => {
                          handleSubmit(data =>
                            onSubmit(data, 'save', menu?.index)
                          )()
                        },
                        variant: 'contained',
                        color: 'primary',
                      },
                    },
                  ],
          },
        }}
      />
    </>
  )
}
